import dayjs from "dayjs";
import { ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import { cn } from "../../handlers/cn.utils";
import EditableTD from "../EditableTD";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Icons } from "../ui/icons";
import { ScrollArea } from "../ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const DataTable = ({ columns, data, setData, setSearchTerm, actions }) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
	const sortedData = React.useMemo(() => {
		if (!sortConfig.key) return data;
		return [...data].sort((a, b) => {
			const aVal = a[sortConfig.key];
			const bVal = b[sortConfig.key];
			if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
			if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
			return 0;
		});
	}, [data, sortConfig]);

	const totalItems = sortedData.length;
	const totalPages = Math.ceil(totalItems / pageSize);
	const startIndex = (currentPage - 1) * pageSize;
	const endIndex = startIndex + pageSize;
	const paginatedData = sortedData.slice(startIndex, endIndex);

	const goToPreviousPage = () => {
		setCurrentPage((prev) => Math.max(1, prev - 1));
	};

	const goToNextPage = () => {
		setCurrentPage((prev) => Math.min(totalPages, prev + 1));
	};

	const handlePageSizeChange = (size) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	const handleSort = (key) => {
		setSortConfig((prev) => ({
			key,
			direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
		}));
	};

	return (
		<div className="flex flex-col gap-4">
			<ScrollArea className="h-full" orientation="both" data-state="hidden">
				<Table>
					<TableHeader>
						<TableRow>
							{columns.map((column) => (
								<TableHead
									key={column.accessor}
									className="cursor-pointer hover:bg-gray-50 select-none"
									onClick={() => handleSort(column.accessor)}
								>
									<div className="flex items-center gap-1">
										{column.header}
										{sortConfig.key === column.accessor && (
											<ChevronDown
												className={cn("h-4 w-4 transition-transform", sortConfig.direction === "asc" && "rotate-180")}
											/>
										)}
									</div>
								</TableHead>
							))}
							{actions && actions.length > 0 && (
								<TableHead width="80px" isLast className="sticky right-0 bg-transparent overflow-hidden">
									<div
										className="absolute inset-0 bg-linear-to-l from-gray-50 via-gray-50 to-transparent"
										style={{ width: "150%", left: "-50%" }}
									/>
								</TableHead>
							)}
						</TableRow>
					</TableHeader>
					<TableBody>
						{paginatedData.map((row, index) => (
							<TableRow key={index} className="group hover:bg-gray-50 transition-all duration-200 border text-nowrap">
								{columns.map((column) => {
									if (
										column.accessor === "scheduledAt" ||
										column.accessor === "sentAt" ||
										column.accessor === "openAt"
									) {
										return (
											<TableCell key={column.accessor}>
												{row[column.accessor] && dayjs(row[column.accessor].$date).format("MMM DD YYYY HH:MM:ss")}
											</TableCell>
										);
									}

									if (column.accessor === "candidate") {
										return (
											<TableCell key={column.accessor}>
												<div className="col-span-2 flex items-center">
													<div className="w-8 h-8 rounded-full mr-3 overflow-hidden">
														<img src={row?.profile_image} alt={row?.full_name} className="w-full h-full object-cover" />
													</div>
													<div>
														<div className="flex items-center">
															<span className="text-sm font-medium text-gray-900">{row?.full_name}</span>
														</div>
														<div className="text-xs text-gray-500">{row?.email}</div>
													</div>
												</div>
											</TableCell>
										);
									}

									if (column.accessor === "emailType") {
										return (
											<TableCell key={column.accessor}>
												<Badge variant="open">{row[column.accessor].replaceAll("_", " ")}</Badge>
											</TableCell>
										);
									}

									if (column.accessor === "opened") {
										return (
											<TableCell key={column.accessor}>
												<Badge variant={row[column.accessor] ? "open" : "default"}>
													{row[column.accessor] ? "YES" : "NO"}
												</Badge>
											</TableCell>
										);
									}

									if (!column.editable || row[column.accessor] !== undefined) {
										const cellValue = column.render ? column.render(row[column.accessor]) : row[column.accessor];
										return <TableCell key={column.accessor}>{cellValue}</TableCell>;
									}

									return (
										<EditableTD
											key={column.accessor}
											value={row[column.accessor]}
											onChange={(newValue) => {
												if (setData) {
													// Check if editing the first column and value already exists
													if (
														column.accessor === columns[0].accessor &&
														data.some(
															(r, i) =>
																i !== index &&
																String(r[column.accessor]).toLowerCase() === String(newValue).toLowerCase(),
														)
													) {
														// Filter all data whose value for the first key is undefined
														const firstKey = columns[0].accessor;
														const filteredData = data.filter((r) => r[firstKey] !== undefined);
														// If setSearchTerm is provided, call it with the duplicate value
														if (typeof setSearchTerm === "function") {
															setSearchTerm(String(newValue));
															setData(filteredData);
														}
														return;
													}
													const updatedData = data.map((r, i) =>
														i === index ? { ...r, [column.accessor]: newValue } : r,
													);
													setData(updatedData);
												}
											}}
											isFirstRow={index === 0}
										/>
									);
								})}
								{actions && actions.length > 0 && (
									<td
										className="sticky right-0 w-[180px] pr-6 overflow-hidden bg-transparent h-9"
										style={{
											borderRight: "none",
											zIndex: 1,
										}}
									>
										<div
											className="absolute inset-0 bg-linear-to-l from-white via-white to-transparent group-hover:from-gray-50 group-hover:via-gray-50"
											style={{ width: "110%", left: "-10%" }}
										/>
										<div className="relative flex items-center justify-end gap-2">
											<Button
												variant="outline"
												size="sm"
												className={cn(
													"h-8 px-3 text-[13px] whitespace-nowrap gap-1.5 transition-all duration-200",
													"border border-gray-200 text-gray-700",
													"hover:bg-gray-50 hover:text-gray-900",
													"opacity-0 group-hover:opacity-100",
													"group-hover:bg-gray-900 group-hover:text-white group-hover:border-gray-900",
													"hover:group-hover:bg-gray-800",
												)}
												onClick={(e) => {
													e.stopPropagation();
													actions[0].onClick(row);
												}}
											>
												Details
												<ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
											</Button>
											<div className="relative isolate flex rounded-lg border border-gray-200 divide-x divide-gray-200 bg-white">
												<DropdownMenu>
													<DropdownMenuTrigger asChild>
														<Button
															variant="ghost"
															className="h-8 w-8 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
														>
															<Icons.MoreHorizontal className="h-4 w-4" />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end" className="w-[200px]">
														{actions.map((action, idx) => (
															<DropdownMenuItem
																key={idx}
																className="text-[13px] whitespace-nowrap"
																onClick={() => action.onClick(row)}
															>
																<action.icon className="mr-2 h-4 w-4" />
																{action.label}
															</DropdownMenuItem>
														))}
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</div>
									</td>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</ScrollArea>

			{/* Pagination Controls */}
			<div className="flex items-center justify-between px-4 py-2">
				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-700">
						Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items
					</span>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="sm" className="h-8">
								{pageSize} per page
								<ChevronDown className="ml-2 h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							{[5, 10, 25].map((size) => (
								<DropdownMenuItem key={size} onClick={() => handlePageSizeChange(size)}>
									{size} per page
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={goToPreviousPage}
						disabled={currentPage === 1}
						className="h-8 w-8 p-0"
					>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="text-sm text-gray-700">
						Page {currentPage} of {totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={goToNextPage}
						disabled={currentPage === totalPages}
						className="h-8 w-8 p-0"
					>
						<ChevronRight className="h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
};

export default DataTable;
